import { HorizontalLayout, VerticalLayout } from '../../common/common-layout'
import MainLayout, { MAIN_RIGHT_TOP_HEIGHT } from '../MainLayout';
import { observer } from 'mobx-react-lite';
import { TitleLargeMedium, TitleNormal } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import theme from '../../common/theme';
import BTButton from '../../common/components/BTButton';
import { useLocation, useNavigate } from 'react-router-dom';
import { TextField } from '@mui/material';
import { AccountChangePasswordFormValues, accountChangePasswordSchema } from '../../schema/homepage/AccountChangePasswordSchema';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { requestChangePassword } from '../../service/AccountService';
import { dialogStore } from '../../store/DialogStore';
import DialogIconInfo from '../../assets/icons/dialog_icon_info.svg';
import isFirebaseError from '../../common/firebase/FirebaseError';

const AccountChangePassword = () => {
    return (
        <MainLayout>
            <AccountChangePasswordBody />
        </MainLayout>
    );
};

export default AccountChangePassword;

const AccountChangePasswordBody = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const { username, email } = location.state || {};

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch
    } = useForm<AccountChangePasswordFormValues>({
        resolver: zodResolver(accountChangePasswordSchema),
    });

    const handleSave = async () => {
        const oldPassword = watch('oldPassword');
        const newPassword = watch('newPassword');
        const confirmNewPassword = watch('confirmNewPassword');
        
        if (oldPassword && oldPassword !== "" && newPassword && newPassword !== "" && newPassword === confirmNewPassword) {
            try {
                await requestChangePassword(email, oldPassword, newPassword);
                dialogStore.showDialog({
                    title: '알림',
                    message: '비밀번호 변경이 완료되었습니다.',
                    icon: <img src={DialogIconInfo} />,
                    onConfirm: () => {
                        navigate('/account');
                    },
                    onClose: () => {
                        navigate('/account');
                    },
                });
                return;
            } catch (error: any) {
                if (isFirebaseError(error)) {
                    if (error.code === "auth/invalid-credential") {
                        dialogStore.showDialog({
                            title: '알림',
                            message: '기존 비밀번호가 일치하지 않습니다.',
                            onConfirm: () => {
                                console.log('확인');
                            },
                            onClose: () => {
                                console.log('닫기');
                            },
                        });
                        return;
                    }
                } else {
                    dialogStore.showDialog({
                        title: '알림',
                        message: `비밀번호 변경에 실패하였습니다 - 사유: ${error}`,
                        onConfirm: () => {
                            console.log('확인');
                        },
                        onClose: () => {
                            console.log('닫기');
                        },
                    });
                    return;
                }
            }
        } else if (!oldPassword || oldPassword === "") {
            dialogStore.showDialog({
                title: '알림',
                message: '기존 비밀번호를 입력해주세요.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        } else if (!newPassword || newPassword === "") {
            dialogStore.showDialog({
                title: '알림',
                message: '신규 비밀번호를 입력해주세요.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        } else if (newPassword !== confirmNewPassword) {
            dialogStore.showDialog({
                title: '알림',
                message: '신규 비밀번호가 일치하지 않습니다.',
                onConfirm: () => {
                    console.log('확인');
                },
                onClose: () => {
                    console.log('닫기');
                },
            });
            return;
        }

        dialogStore.showDialog({
            title: '알림',
            message: '비밀번호 변경에 실패하였습니다.',
            icon: <img src={DialogIconInfo} />,
            onConfirm: () => {
                console.log('확인');
            },
            onClose: () => {
                console.log('닫기');
            },
        });
    };

    const handleCancel = (): void => {
        navigate(-1);
    };

    return (
        <div style={{ width: '100%', minHeight: `calc(100vh - ${MAIN_RIGHT_TOP_HEIGHT})`, display: 'flex', flexDirection: 'column' }} >
            <VerticalLayout height={'auto'} paddingLeft={36} paddingRight={36}>
                <form style={{ width: 516 }}>
                    <TitleLargeMedium>관리자 계정 비밀번호 변경</TitleLargeMedium>
                    <BTBox height='36px' />
                    <div style={{width: '678px'}}>
                        <TitleNormal color='#8a92a6'>아이디</TitleNormal>
                        <BTBox height='8px' />
                        <TextField
                                variant="outlined"
                                fullWidth
                                value={username || ""}
                                disabled
                        />
                        <BTBox height='16px' />
                        <TitleNormal color='#8a92a6'>이메일</TitleNormal>
                        <BTBox height='8px' />
                        <TextField
                                variant="outlined"
                                fullWidth
                                value={email || ""}
                                disabled
                        />
                        <BTBox height='16px' />
                        <TitleNormal color='#8a92a6'>기존 비밀번호</TitleNormal>
                        <BTBox height='8px' />
                        <HorizontalLayout>
                            <TextField
                                variant="outlined"
                                fullWidth
                                type="password"
                                {...register("oldPassword")}
                                error={!!errors.oldPassword}
                                helperText={errors.oldPassword?.message}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: errors.oldPassword ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                    },
                                }}
                            />
                        </HorizontalLayout>
                        <BTBox height='16px' />
                        <TitleNormal color='#8a92a6'>신규 비밀번호</TitleNormal>
                        <BTBox height='8px' />
                        <HorizontalLayout>
                            <TextField
                                variant="outlined"
                                fullWidth
                                type="password"
                                {...register("newPassword")}
                                error={!!errors.newPassword}
                                helperText={errors.newPassword?.message}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: errors.newPassword ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                    },
                                }}
                            />
                        </HorizontalLayout>
                        <BTBox height='16px' />
                        <TitleNormal color='#8a92a6'>신규 비밀번호 확인</TitleNormal>
                        <BTBox height='8px' />
                        <HorizontalLayout>
                            <TextField
                                variant="outlined"
                                fullWidth
                                type="password"
                                {...register("confirmNewPassword")}
                                error={!!errors.confirmNewPassword}
                                helperText={errors.confirmNewPassword?.message}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: errors.confirmNewPassword ? '#c03221' : theme.colors.bbTech, // 원하는 색상
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.colors.bbTech,
                                        },
                                    },
                                }}
                            />
                        </HorizontalLayout>
                    </div>
                </form>
            </VerticalLayout>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingRight: '76px', paddingBottom: '76px' }}>
                <HorizontalLayout style={{ marginTop: 'auto', justifyContent: 'flex-end' }}>
                    <BTButton color={theme.colors.bbTech} textColor='white' onClick={handleSave}>저장</BTButton>
                    <BTBox width='24px' />
                    <BTButton onClick={handleCancel}>취소</BTButton>
                </HorizontalLayout>
            </div>
        </div>

    );
});

